import { Link } from 'react-router-dom'

import { AdvancedImage } from '@/components/ui/advanced-image'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardModel } from '@/types'
import { getFileUrl } from '@/clients/http'
import { useBusiness } from '@/contexts/BusinessContext'

interface TopProductsProps {
  productVariations: DashboardModel.TopSellingProduct[]
  className?: string
}

/** Best sellers with a bar showing each one's sales relative to the top product. */
export const TopProducts = ({ productVariations, className }: TopProductsProps) => {
  const { formatCurrency } = useBusiness()
  const items = productVariations ?? []
  const max = Math.max(1, ...items.map((v) => v.sales || 0))

  return (
    <Card className={className}>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">Top selling products</CardTitle>
          <CardDescription className="mt-1 text-xs">By units sold</CardDescription>
        </div>
        <Button variant="link" size="sm" asChild>
          <Link to="/products">View all</Link>
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No sales yet.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((v, i) => (
              <li key={`${v.sku}-${i}`} className="flex items-center gap-3">
                <AdvancedImage
                  src={v.image ? getFileUrl(v.image) : ''}
                  alt=""
                  className="size-10 shrink-0 rounded-md border border-border bg-subdued"
                  fallbackText="Img"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="truncate text-sm font-medium text-ink">{v.name}</p>
                    <p className="shrink-0 text-sm tabular-nums text-ink">{formatCurrency(Number(v.price))}</p>
                  </div>
                  <div className="mt-1.5 flex items-center gap-3">
                    <div className="h-2 flex-1 overflow-hidden rounded-sm bg-muted">
                      <div className="h-full rounded-sm bg-chart-1" style={{ width: `${((v.sales || 0) / max) * 100}%` }} />
                    </div>
                    <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{v.sales || 0} sold</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
